/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var MgtPersonCard_1;
import { customElement, html, internalProperty, property } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { MgtTemplatedComponent, Providers, ProviderState, TeamsHelper } from '@microsoft/mgt-element';
import { findPeople, getEmailFromGraphEntity } from '../../graph/graph.people';
import { ViewType } from '../../graph/types';
import { getPersonImage } from '../../graph/graph.photos';
import { getUserWithPhoto } from '../../graph/graph.userWithPhoto';
import { getSvg, SvgIcon } from '../../utils/SvgHelper';
import { getUserPresence } from '../../graph/graph.presence';
import { getPersonCardGraphData } from './mgt-person-card.graph';
import { styles } from './mgt-person-card-css';
import { MgtPersonCardContact } from './sections/mgt-person-card-contact/mgt-person-card-contact';
import { MgtPersonCardFiles } from './sections/mgt-person-card-files/mgt-person-card-files';
import { MgtPersonCardMessages } from './sections/mgt-person-card-messages/mgt-person-card-messages';
import { MgtPersonCardOrganization } from './sections/mgt-person-card-organization/mgt-person-card-organization';
import { MgtPersonCardProfile } from './sections/mgt-person-card-profile/mgt-person-card-profile';
import { strings } from './strings';
import '../sub-components/mgt-spinner/mgt-spinner';
export * from './mgt-person-card.types';
/**
 * Web Component used to show detailed data for a person in the Microsoft Graph
 *
 * @export
 * @class MgtPersonCard
 * @extends {MgtTemplatedComponent}
 *
 * @fires expanded - Fired when expanded details section is opened
 *
 * @cssprop --person-card-display-name-font-size - {Length} Font size of display name title
 * @cssprop --person-card-display-name-color - {Color} Color of display name font
 * @cssprop --person-card-title-font-size - {Length} Font size of title
 * @cssprop --person-card-title-color - {Color} Color of title
 * @cssprop --person-card-subtitle-font-size - {Length} Font size of subtitle
 * @cssprop --person-card-subtitle-color - {Color} Color of subttitle
 * @cssprop --person-card-details-title-font-size - {Length} Font size additional details title
 * @cssprop --person-card-details-title-color- {Color} Color of additional details title
 * @cssprop --person-card-details-item-font-size - {Length} Font size items in additional details section
 * @cssprop --person-card-details-item-color - {Color} Color of items in additional details section
 * @cssprop --person-card-background-color - {Color} Color of person card background
 */
let MgtPersonCard = MgtPersonCard_1 = class MgtPersonCard extends MgtTemplatedComponent {
    /**
     * Array of styles to apply to the element. The styles should be defined
     * using the `css` tag function.
     */
    static get styles() {
        return styles;
    }
    get strings() {
        return strings;
    }
    /**
     * Get the scopes required for the person card
     * The scopes depend on what sections are shown
     *
     * Use the `MgtPersonCard.config` object to configure
     * what sections are shown
     *
     * @static
     * @return {*}  {string[]}
     * @memberof MgtPersonCard
     */
    static get requiredScopes() {
        return MgtPersonCard_1.getScopes();
    }
    static getScopes() {
        const scopes = [];
        if (this.config.sections.files) {
            scopes.push('Sites.Read.All');
        }
        if (this.config.sections.mailMessages) {
            scopes.push('Mail.ReadBasic');
        }
        if (this.config.sections.organization) {
            scopes.push('User.Read.All');
            if (typeof this.config.sections.organization !== 'boolean' && this.config.sections.organization.showWorksWith) {
                scopes.push('People.Read.All');
            }
        }
        if (this.config.sections.profile) {
            scopes.push('User.Read.All');
        }
        if (this.config.useContactApis) {
            scopes.push('Contacts.Read');
        }
        if (scopes.indexOf('User.Read.All') < 0) {
            // at minimum, we need these scopes
            scopes.push('User.ReadBasic.All');
            scopes.push('User.Read');
        }
        if (scopes.indexOf('People.Read.All') < 0) {
            // at minimum, we need these scopes
            scopes.push('People.Read');
        }
        // return unique
        return [...new Set(scopes)];
    }
    /**
     * Global configuration object for
     * all person card components
     *
     * @static
     * @type {MgtPersonCardConfig}
     * @memberof MgtPersonCard
     */
    static get config() {
        return this._config;
    }
    /**
     * Set the person details to render
     *
     * @type {IDynamicPerson}
     * @memberof MgtPersonCard
     */
    get personDetails() {
        return this._personDetails;
    }
    set personDetails(value) {
        if (this._personDetails === value) {
            return;
        }
        this._personDetails = value;
        this.personImage = this.getImage();
        this.requestStateUpdate();
    }
    /**
     * user-id property allows developer to use id value for component
     * @type {string}
     */
    get userId() {
        return this._userId;
    }
    set userId(value) {
        if (value === this._userId) {
            return;
        }
        this._userId = value;
        this.personDetails = null;
        this.state = null;
        this.requestStateUpdate();
    }
    get internalPersonDetails() {
        return (this.state && this.state.person) || this.personDetails;
    }
    constructor() {
        super();
        this._chatInput = '';
        this._currentSection = null;
        this._history = [];
        this.sections = [];
    }
    /**
     * Synchronizes property values when attributes change.
     *
     * @param {*} name
     * @param {*} oldValue
     * @param {*} newValue
     * @memberof MgtPersonCard
     */
    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);
        if (oldValue === newValue) {
            return;
        }
        switch (name) {
            case 'person-query':
                this.personDetails = null;
                this.state = null;
                this.requestStateUpdate();
                break;
        }
    }
    /**
     * Navigate the card to a different person.
     *
     * @protected
     * @memberof MgtPersonCard
     */
    navigate(person) {
        this._history.push({
            personDetails: this.personDetails,
            personImage: this.getImage(),
            state: this.state
        });
        this._personDetails = person;
        this.state = null;
        this.personImage = null;
        this._currentSection = null;
        this.sections = [];
        this.requestStateUpdate();
    }
    /**
     * Navigate the card back to the previous person
     *
     * @returns {void}
     * @memberof MgtPersonCard
     */
    goBack() {
        if (!this._history || !this._history.length) {
            return;
        }
        const historyState = this._history.pop();
        this._currentSection = null;
        this.state = historyState.state;
        this._personDetails = historyState.state;
        this.personImage = historyState.personImage;
        this.loadSections();
    }
    /**
     * Navigate the card back to first person and clear history
     *
     * @returns {void}
     * @memberof MgtPersonCard
     */
    clearHistory() {
        this._currentSection = null;
        if (!this._history || !this._history.length) {
            return;
        }
        const historyState = this._history[0];
        this._history = [];
        this.state = historyState.state;
        this._personDetails = historyState.state;
        this.personImage = historyState.personImage;
        this.loadSections();
    }
    /**
     * Invoked on each update to perform rendering tasks. This method must return
     * a lit-html TemplateResult. Setting properties inside this method will *not*
     * trigger the element to update.
     */
    render() {
        // Handle no data
        if (!this.internalPersonDetails) {
            return this.renderNoData();
        }
        const person = this.internalPersonDetails;
        const image = this.getImage();
        // Check for a default template.
        // tslint:disable-next-line: no-string-literal
        if (this.hasTemplate('default')) {
            return this.renderTemplate('default', {
                person: this.internalPersonDetails,
                personImage: image
            });
        }
        const navigationTemplate = this._history && this._history.length
            ? html `
            <div class="nav">
              <div class="nav__back" tabindex="0" @keydown=${(e) => {
                e.code === 'Enter' ? this.goBack() : '';
            }} @click=${() => this.goBack()}>${getSvg(SvgIcon.Back)}</div>
            </div>
          `
            : null;
        // Check for a person-details template
        let personDetailsTemplate = this.renderTemplate('person-details', {
            person: this.internalPersonDetails,
            personImage: image
        });
        if (!personDetailsTemplate) {
            const personTemplate = this.renderPerson();
            const contactIconsTemplate = this.renderContactIcons(person);
            personDetailsTemplate = html `
        ${personTemplate} ${contactIconsTemplate}
      `;
        }
        const expandedDetailsTemplate = this.isExpanded ? this.renderExpandedDetails() : this.renderExpandedDetailsButton();
        this._windowHeight =
            window.innerHeight && document.documentElement.clientHeight
                ? Math.min(window.innerHeight, document.documentElement.clientHeight)
                : window.innerHeight || document.documentElement.clientHeight;
        if (this._windowHeight < 250) {
            this._smallView = true;
        }
        const tabLocker = this.lockTabNavigation
            ? html `<div @keydown=${this.handleEndOfCard} aria-label=${this.strings.endOfCard} tabindex="0" id="end-of-container"></div>`
            : html ``;
        return html `
      <div class="root" dir=${this.direction}>
      <div class=${this._smallView ? 'small' : ''}>
          ${navigationTemplate}
          <div class="person-details-container">${personDetailsTemplate}</div>
          <div class="expanded-details-container">${expandedDetailsTemplate}</div>
          ${tabLocker}
        </div>
      </div>
    `;
    }
    handleEndOfCard(e) {
        if (e && e.code === 'Tab') {
            const endOfCardEl = this.renderRoot.querySelector('#end-of-container');
            if (endOfCardEl) {
                endOfCardEl.blur();
                const imageCardEl = this.renderRoot.querySelector('mgt-person');
                if (imageCardEl) {
                    imageCardEl.focus();
                }
            }
        }
    }
    /**
     * Render the state when no data is available.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderNoData() {
        return this.renderTemplate('no-data', null) || html ``;
    }
    /**
     * Render a display image for the person.
     *
     * @protected
     * @param {*} image
     * @memberof MgtPersonCard
     */
    renderPerson() {
        const avatarSize = 'large';
        return html `
      <mgt-person
        tabindex="0"
        class="person-image"
        .personDetails=${this.internalPersonDetails}
        .personImage=${this.getImage()}
        .personPresence=${this.personPresence}
        .showPresence=${this.showPresence}
        .avatarSize=${avatarSize}
        .view=${ViewType.threelines}
        .line2Property=${'jobTitle'}
        .line3Property=${'department'}
      ></mgt-person>
    `;
    }
    /**
     * Render person subtitle.
     *
     * @protected
     * @param {IDynamicPerson} person
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderPersonSubtitle(person) {
        person = person || this.internalPersonDetails;
        if (!person.department) {
            return;
        }
        return html `
      <div class="department">${person.department}</div>
    `;
    }
    /**
     * Render the various icons for contacting the person.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderContactIcons(person) {
        if (this.isExpanded) {
            return;
        }
        person = person || this.internalPersonDetails;
        const userPerson = person;
        // Email
        let email;
        if (getEmailFromGraphEntity(person)) {
            email = html `
        <div class="icon" @click=${() => this.emailUser()} tabindex=0>
          ${getSvg(SvgIcon.SmallEmail)}
          <span>${this.strings.sendEmailLinkSubtitle}</span>
        </div>
      `;
        }
        // Chat
        let chat;
        if (userPerson.userPrincipalName) {
            chat = html `
        <div class="icon" @click=${() => this.chatUser()} tabindex=0>
          ${getSvg(SvgIcon.SmallChat)}
          <span>${this.strings.startChatLinkSubtitle}</span>
        </div>
      `;
        }
        return html `
      <div class="base-icons">
        ${email} ${chat}
      </div>
    `;
    }
    /**
     * Render the button used to expand the expanded details.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderExpandedDetailsButton() {
        return html `
      <div 
        class="expanded-details-button" 
        @click=${() => this.showExpandedDetails()} 
        @keydown=${this.handleKeyDown}
        tabindex=0>
          ${getSvg(SvgIcon.ExpandDown)}
      </div>
    `;
    }
    /**
     * Render expanded details.
     *
     * @protected
     * @param {IDynamicPerson} [person]
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderExpandedDetails(person) {
        if (!this.state && this.isStateLoading) {
            return html `
        <div class="loading">
          <mgt-spinner></mgt-spinner>
        </div>
      `;
        }
        // load sections when details are expanded
        // when not singed in
        const provider = Providers.globalProvider;
        if (provider.state === ProviderState.SignedOut) {
            this.loadSections();
        }
        person = person || this.internalPersonDetails;
        const sectionNavTemplate = this.renderSectionNavigation();
        const currentSectionTemplate = this.renderCurrentSection();
        return html `
      <div class="section-nav">
        ${sectionNavTemplate}
      </div>
      <div class="section-host ${this._smallView ? 'small' : ''}" @wheel=${(e) => this.handleSectionScroll(e)} tabindex=0>
        ${currentSectionTemplate}
      </div>
    `;
    }
    /**
     * Render the navigation ribbon for subsections
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderSectionNavigation() {
        if (!this.sections || (this.sections.length < 2 && !this.hasTemplate('additional-details'))) {
            return;
        }
        const currentSectionIndex = this._currentSection ? this.sections.indexOf(this._currentSection) : -1;
        const navIcons = this.sections.map((section, i) => {
            const classes = classMap({
                active: i === currentSectionIndex,
                'section-nav__icon': true
            });
            const tagName = section.tagName;
            const ariaLabel = tagName.substring(16, tagName.length).toLowerCase();
            return html `
        <button
          aria-label=${ariaLabel}
          tabindex=0
          class=${classes}
          @click=${() => this.updateCurrentSection(section)}>
            ${section.renderIcon()}</button>
      `;
        });
        const overviewClasses = classMap({
            active: currentSectionIndex === -1,
            'section-nav__icon': true
        });
        return html `
      <button 
        aria-label="overview"
        tabindex=0 
        class=${overviewClasses}
        @click=${() => this.updateCurrentSection(null)}>
          ${getSvg(SvgIcon.Overview)}
      </button>
      ${navIcons}
    `;
    }
    /**
     * Render the default section with compact views for each subsection.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderOverviewSection() {
        function handleKeyDown(e, section) {
            e.code === 'Enter' ? this.updateCurrentSection(section) : '';
        }
        const compactTemplates = this.sections.map((section) => html `
        <div class="section">
          <div class="section__header">
            <div class="section__title" tabindex="0">${section.displayName}</div>
            <a 
              class="section__show-more"
              tabindex=0
              @keydown=${(e) => handleKeyDown(e, section)}
              @click=${() => this.updateCurrentSection(section)}>
                ${this.strings.showMoreSectionButton}</a>
          </div>
          <div class="section__content">${section.asCompactView()}</div>
        </div>
      `);
        const additionalDetails = this.renderTemplate('additional-details', {
            person: this.internalPersonDetails,
            personImage: this.getImage(),
            state: this.state
        });
        if (additionalDetails) {
            compactTemplates.splice(1, 0, html `
          <div class="section">
            <div class="additional-details">${additionalDetails}</div>
          </div>
        `);
        }
        // To be included when support for direct messaging is added
        // ${this.internalPersonDetails.id !== this._me.id && MgtPersonCard.config.isSendMessageVisible
        //   ? html`
        //       <div class="quick-message">
        //         <input
        //           type="text"
        //           class="quick-message__input"
        //           placeholder="Message ${this.internalPersonDetails.displayName}"
        //           .value=${this._chatInput}
        //           @input=${(e: Event) => {
        //             this._chatInput = (e.target as HTMLInputElement).value;
        //           }}
        //         />
        //         <button class="quick-message__send" @click=${() => this.sendQuickMessage()}>
        //           ${getSvg(SvgIcon.Send)}
        //         </button>
        //       </div>
        //     `
        //   : null}
        return html `
      <div class="sections">
        ${compactTemplates}
      </div>
    `;
    }
    /**
     * Render the actively selected section.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPersonCard
     */
    renderCurrentSection() {
        if ((!this.sections || !this.sections.length) && !this.hasTemplate('additional-details')) {
            return;
        }
        if (this.sections.length === 1 && !this.hasTemplate('additional-details')) {
            return html `
        ${this.sections[0].asFullView()}
      `;
        }
        if (!this._currentSection) {
            return this.renderOverviewSection();
        }
        return html `
      ${this._currentSection.asFullView()}
    `;
    }
    /**
     * load state into the component
     *
     * @protected
     * @returns
     * @memberof MgtPersonCard
     */
    loadState() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state) {
                return;
            }
            if (!this.personDetails && this.inheritDetails) {
                // User person details inherited from parent tree
                let parent = this.parentElement;
                while (parent && parent.tagName !== 'MGT-PERSON') {
                    parent = parent.parentElement;
                }
                let parentPerson = parent.personDetails || parent['personDetailsInternal'];
                if (parent && parentPerson) {
                    this.personDetails = parentPerson;
                    this.personImage = parent.personImage;
                }
            }
            const provider = Providers.globalProvider;
            // check if user is signed in
            if (!provider || provider.state !== ProviderState.SignedIn) {
                return;
            }
            const graph = provider.graph.forComponent(this);
            this.isStateLoading = true;
            if (!this._me) {
                this._me = yield Providers.me();
            }
            // check if personDetail already populated
            if (this.personDetails) {
                const user = this.personDetails;
                const id = user.userPrincipalName || user.id;
                // if we have an id but no email, we should get data from the graph
                // in some graph calls, the user object does not contain the email
                if (id && !getEmailFromGraphEntity(user)) {
                    const person = yield getUserWithPhoto(graph, id);
                    this.personDetails = person;
                    this.personImage = this.getImage();
                }
            }
            else if (this.userId || this.personQuery === 'me') {
                // Use userId or 'me' query to get the person and image
                const person = yield getUserWithPhoto(graph, this.userId);
                this.personDetails = person;
                this.personImage = this.getImage();
            }
            else if (this.personQuery) {
                // Use the personQuery to find our person.
                const people = yield findPeople(graph, this.personQuery, 1);
                if (people && people.length) {
                    this.personDetails = people[0];
                    getPersonImage(graph, this.personDetails, MgtPersonCard_1.config.useContactApis).then(image => {
                        if (image) {
                            this.personDetails.personImage = image;
                            this.personImage = image;
                        }
                    });
                }
            }
            // populate presence
            const defaultPresence = {
                activity: 'Offline',
                availability: 'Offline',
                id: null
            };
            if (!this.personPresence && this.showPresence) {
                try {
                    if (this.personDetails && this.personDetails.id) {
                        getUserPresence(graph, this.personDetails.id).then(presence => {
                            this.personPresence = presence;
                        });
                    }
                    else {
                        this.personPresence = defaultPresence;
                    }
                }
                catch (_) {
                    // set up a default Presence in case beta api changes or getting error code
                    this.personPresence = defaultPresence;
                }
            }
            // populate state
            if ((_a = this.personDetails) === null || _a === void 0 ? void 0 : _a.id) {
                this.state = yield getPersonCardGraphData(graph, this.personDetails, this._me === this.personDetails.id, MgtPersonCard_1.config);
            }
            this.loadSections();
            this.isStateLoading = false;
        });
    }
    /**
     * Send a chat message to the user from the quick message input.
     *
     * @protected
     * @returns {void}
     * @memberof MgtPersonCard
     */
    sendQuickMessage() {
        const message = this._chatInput.trim();
        if (!message || !message.length) {
            return;
        }
        this.chatUser(message);
    }
    /**
     * Use the mailto: protocol to initiate a new email to the user.
     *
     * @protected
     * @memberof MgtPersonCard
     */
    emailUser() {
        const user = this.internalPersonDetails;
        if (user) {
            const email = getEmailFromGraphEntity(user);
            if (email) {
                window.open('mailto:' + email, '_blank', 'noreferrer');
            }
        }
    }
    /**
     * Use the tel: protocol to initiate a new call to the user.
     *
     * @protected
     * @memberof MgtPersonCard
     */
    callUser() {
        const user = this.personDetails;
        const person = this.personDetails;
        if (user && user.businessPhones && user.businessPhones.length) {
            const phone = user.businessPhones[0];
            if (phone) {
                window.open('tel:' + phone, '_blank', 'noreferrer');
            }
        }
        else if (person && person.phones && person.phones.length) {
            const businessPhones = this.getPersonBusinessPhones(person);
            const phone = businessPhones[0];
            if (phone) {
                window.open('tel:' + phone, '_blank', 'noreferrer');
            }
        }
    }
    /**
     * Initiate a chat message to the user via deeplink.
     *
     * @protected
     * @memberof MgtPersonCard
     */
    chatUser(message = null) {
        const user = this.personDetails;
        if (user && user.userPrincipalName) {
            const users = user.userPrincipalName;
            let url = `https://teams.microsoft.com/l/chat/0/0?users=${users}`;
            if (message && message.length) {
                url += `&message=${message}`;
            }
            const openWindow = () => window.open(url, '_blank', 'noreferrer');
            if (TeamsHelper.isAvailable) {
                TeamsHelper.executeDeepLink(url, (status) => {
                    if (!status) {
                        openWindow();
                    }
                });
            }
            else {
                openWindow();
            }
        }
    }
    /**
     * Display the expanded details panel.
     *
     * @protected
     * @memberof MgtPersonCard
     */
    showExpandedDetails() {
        const root = this.renderRoot.querySelector('.root');
        if (root && root.animate) {
            // play back
            root.animate([
                {
                    height: 'auto',
                    transformOrigin: 'top left'
                },
                {
                    height: 'auto',
                    transformOrigin: 'top left'
                }
            ], {
                duration: 1000,
                easing: 'ease-in-out',
                fill: 'both'
            });
        }
        this.isExpanded = true;
        this.fireCustomEvent('expanded', null, true);
    }
    loadSections() {
        this.sections = [];
        if (!this.internalPersonDetails) {
            return;
        }
        const contactSections = new MgtPersonCardContact(this.internalPersonDetails);
        if (contactSections.hasData) {
            this.sections.push(contactSections);
        }
        if (!this.state) {
            return;
        }
        const { person, directReports, messages, files, profile } = this.state;
        if (MgtPersonCard_1.config.sections.organization &&
            ((person && person.manager) || (directReports && directReports.length))) {
            this.sections.push(new MgtPersonCardOrganization(this.state, this._me));
        }
        if (MgtPersonCard_1.config.sections.mailMessages && messages && messages.length) {
            this.sections.push(new MgtPersonCardMessages(messages));
        }
        if (MgtPersonCard_1.config.sections.files && files && files.length) {
            this.sections.push(new MgtPersonCardFiles(files));
        }
        if (MgtPersonCard_1.config.sections.profile && profile) {
            const profileSection = new MgtPersonCardProfile(profile);
            if (profileSection.hasData) {
                this.sections.push(profileSection);
            }
        }
    }
    getImage() {
        if (this.personImage) {
            return this.personImage;
        }
        const person = this.personDetails;
        return person && person.personImage ? person.personImage : null;
    }
    getPersonBusinessPhones(person) {
        const phones = person.phones;
        const businessPhones = [];
        for (const p of phones) {
            if (p.type === 'business') {
                businessPhones.push(p.number);
            }
        }
        return businessPhones;
    }
    updateCurrentSection(section) {
        const sectionHost = this.renderRoot.querySelector('.section-host');
        sectionHost.scrollTop = 0;
        this._currentSection = section;
        this.requestUpdate();
    }
    handleSectionScroll(e) {
        const target = this.renderRoot.querySelector('.section-host');
        if (target) {
            if (!(e.deltaY < 0 && target.scrollTop === 0) &&
                !(e.deltaY > 0 && target.clientHeight + target.scrollTop >= target.scrollHeight - 1)) {
                e.stopPropagation();
            }
        }
    }
    handleKeyDown(e) {
        //enter activates person-card
        if (e) {
            if (e.keyCode === 13) {
                this.showExpandedDetails();
            }
        }
    }
};
MgtPersonCard._config = {
    sections: {
        files: true,
        mailMessages: true,
        organization: { showWorksWith: true },
        profile: true
    },
    useContactApis: true
};
__decorate([
    property({
        attribute: 'person-details',
        type: Object
    }),
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [Object])
], MgtPersonCard.prototype, "personDetails", null);
__decorate([
    property({
        attribute: 'person-query'
    }),
    __metadata("design:type", String)
], MgtPersonCard.prototype, "personQuery", void 0);
__decorate([
    property({
        attribute: 'lock-tab-navigation',
        type: Boolean
    }),
    __metadata("design:type", Boolean)
], MgtPersonCard.prototype, "lockTabNavigation", void 0);
__decorate([
    property({
        attribute: 'user-id'
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], MgtPersonCard.prototype, "userId", null);
__decorate([
    property({
        attribute: 'person-image',
        type: String
    }),
    __metadata("design:type", String)
], MgtPersonCard.prototype, "personImage", void 0);
__decorate([
    property({
        attribute: 'fetch-image',
        type: Boolean
    }),
    __metadata("design:type", Boolean)
], MgtPersonCard.prototype, "fetchImage", void 0);
__decorate([
    property({
        attribute: 'is-expanded',
        type: Boolean
    }),
    __metadata("design:type", Boolean)
], MgtPersonCard.prototype, "isExpanded", void 0);
__decorate([
    property({
        attribute: 'inherit-details',
        type: Boolean
    }),
    __metadata("design:type", Boolean)
], MgtPersonCard.prototype, "inheritDetails", void 0);
__decorate([
    property({
        attribute: 'show-presence',
        type: Boolean
    }),
    __metadata("design:type", Boolean)
], MgtPersonCard.prototype, "showPresence", void 0);
__decorate([
    property({
        attribute: 'person-presence',
        type: Object
    }),
    __metadata("design:type", Object)
], MgtPersonCard.prototype, "personPresence", void 0);
__decorate([
    internalProperty(),
    __metadata("design:type", Object)
], MgtPersonCard.prototype, "state", void 0);
__decorate([
    internalProperty(),
    __metadata("design:type", Boolean)
], MgtPersonCard.prototype, "isStateLoading", void 0);
MgtPersonCard = MgtPersonCard_1 = __decorate([
    customElement('mgt-person-card'),
    __metadata("design:paramtypes", [])
], MgtPersonCard);
export { MgtPersonCard };
//# sourceMappingURL=mgt-person-card.js.map